/**
 * F-148 §10.1 list: Drafts / Receipts tabs with keyset "Load more". Each item shows the display
 * reference with its origin badge, supplier, line count, accepted units and last update.
 * The UI says "Receipts", never "stock history" or "ledger".
 */
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ChevronRight, Loader2, PackageOpen } from 'lucide-react';
import { useTranslation } from '@dukanti/shared';
import type { ReceivingListItem } from '@dukanti/shared/admin';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { withRawValues } from '../lib/error-messages';
import { ReferenceBadge } from './reference-badge';

export type ReceivingListKind = 'draft' | 'receipt';

export interface ReceivingListProps {
  readonly kind: ReceivingListKind;
  readonly onKindChange: (kind: ReceivingListKind) => void;
  readonly items: readonly ReceivingListItem[];
  readonly isLoading?: boolean;
  readonly isError?: boolean;
  readonly onRetry?: () => void;
  readonly hasMore?: boolean;
  readonly isLoadingMore?: boolean;
  readonly onLoadMore?: () => void;
  /** Hides the Drafts tab entirely (e.g. paused stores that never had drafts). */
  readonly showDraftsTab?: boolean;
  readonly className?: string;
}

function formatDateTime(iso: string, lang: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(lang, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

// eslint-disable-next-line react-refresh/only-export-components -- route helper co-located with the list (precedent: button.tsx)
export function receivingItemHref(item: Pick<ReceivingListItem, 'kind' | 'id'>): string {
  return item.kind === 'draft'
    ? `/stock/receiving/drafts/${encodeURIComponent(item.id)}`
    : `/stock/receiving/receipts/${encodeURIComponent(item.id)}`;
}

export function ReceivingList({
  kind,
  onKindChange,
  items,
  isLoading = false,
  isError = false,
  onRetry,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  showDraftsTab = true,
  className,
}: ReceivingListProps): React.JSX.Element {
  const { t, lang } = useTranslation();
  const tabs: readonly ReceivingListKind[] = showDraftsTab ? ['draft', 'receipt'] : ['receipt'];
  const tabRefs = useRef<Partial<Record<ReceivingListKind, HTMLButtonElement | null>>>({});

  const onTabKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const index = tabs.indexOf(kind);
    const next = tabs[(index + 1) % tabs.length] ?? kind;
    onKindChange(next);
    // Roving tabindex: focus follows the selection, otherwise focus stays on a `tabIndex=-1` tab.
    tabRefs.current[next]?.focus();
  };

  return (
    <div className={cn('space-y-4 text-start', className)} data-testid="receiving-list">
      <div
        role="tablist"
        aria-label={t('admin.receiving.title')}
        className="border-border flex gap-1 border-b"
        onKeyDown={onTabKeyDown}
      >
        {tabs.map((tab) => {
          const selected = tab === kind;
          return (
            <button
              key={tab}
              ref={(el) => {
                tabRefs.current[tab] = el;
              }}
              type="button"
              role="tab"
              id={`receiving-tab-${tab}`}
              aria-selected={selected}
              aria-controls={`receiving-panel-${tab}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => onKindChange(tab)}
              className={cn(
                'focus-visible:ring-ring/50 -mb-px min-h-11 rounded-t-lg border-b-2 px-4 text-sm font-medium outline-none focus-visible:ring',
                selected
                  ? 'border-admin-lime text-foreground'
                  : 'text-muted-foreground hover:text-foreground border-transparent',
              )}
              data-testid={`receiving-tab-${tab}`}
            >
              {tab === 'draft'
                ? t('admin.receiving.tabs.drafts')
                : t('admin.receiving.tabs.receipts')}
            </button>
          );
        })}
      </div>

      <div
        role="tabpanel"
        id={`receiving-panel-${kind}`}
        aria-labelledby={`receiving-tab-${kind}`}
        className="space-y-3"
        aria-busy={isLoading}
      >
        {isLoading ? (
          <div
            className="border-border bg-card shadow-admin-card overflow-hidden rounded-lg border"
            data-testid="receiving-list-skeleton"
          >
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="border-border flex items-center gap-4 border-b px-4 py-4 last:border-b-0"
              >
                <div className="flex-1 space-y-2">
                  <div className="bg-admin-surface-muted h-3 w-1/3 animate-pulse rounded motion-reduce:animate-none" />
                  <div className="bg-admin-surface-muted h-3 w-1/4 animate-pulse rounded motion-reduce:animate-none" />
                </div>
                <div className="bg-admin-surface-muted h-5 w-16 animate-pulse rounded-full motion-reduce:animate-none" />
              </div>
            ))}
          </div>
        ) : null}

        {isError ? (
          <div
            role="alert"
            className="border-border bg-card shadow-admin-card rounded-lg border p-6 text-center"
            data-testid="receiving-list-error"
          >
            <AlertCircle
              aria-hidden="true"
              className="text-destructive mx-auto mb-3 size-8"
              strokeWidth={1.5}
            />
            <p className="text-muted-foreground mb-4 text-sm">{t('common.errors.networkFailed')}</p>
            {onRetry ? (
              <Button type="button" variant="outline" className="min-h-11" onClick={onRetry}>
                {t('common.retry')}
              </Button>
            ) : null}
          </div>
        ) : null}

        {!isLoading && !isError && items.length === 0 ? (
          <div
            className="border-border bg-card shadow-admin-card rounded-lg border px-6 py-12 text-center"
            data-testid="receiving-list-empty"
          >
            <PackageOpen
              aria-hidden="true"
              className="text-muted-foreground mx-auto mb-3 size-8"
              strokeWidth={1.5}
            />
            <p className="text-muted-foreground text-sm">
              {kind === 'draft'
                ? t('admin.receiving.list.empty')
                : t('admin.receiving.list.emptyReceipts')}
            </p>
          </div>
        ) : null}

        {!isLoading && !isError && items.length > 0 ? (
          <ul className="border-border bg-card shadow-admin-card divide-border divide-y overflow-hidden rounded-lg border">
            {items.map((item) => (
              <li
                key={`${item.kind}:${item.id}`}
                data-testid="receiving-list-item"
                data-kind={item.kind}
                data-id={item.id}
              >
                <Link
                  to={receivingItemHref(item)}
                  className="hover:bg-admin-surface-muted focus-visible:ring-ring/50 flex min-h-16 items-center gap-3 px-4 py-3 outline-none focus-visible:ring"
                >
                  <div className="min-w-0 flex-1 space-y-1">
                    <ReferenceBadge reference={item} showHelp={false} />
                    <p className="text-muted-foreground truncate text-xs">
                      <bdi>{item.supplierName}</bdi>
                      {' · '}
                      {t('admin.receiving.list.lines', { count: item.lineCount })}
                      {item.acceptedUnits !== null ? (
                        <>
                          {' · '}
                          {t('admin.receiving.list.acceptedUnits', { count: item.acceptedUnits })}
                        </>
                      ) : null}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {t(
                        'admin.receiving.list.updated',
                        withRawValues({ date: formatDateTime(item.updatedAt, lang) }),
                      )}
                    </p>
                  </div>
                  <ChevronRight
                    aria-hidden="true"
                    className="text-muted-foreground size-4 shrink-0 rtl:rotate-180"
                  />
                  <span className="sr-only">{t('admin.receiving.list.open')}</span>
                </Link>
              </li>
            ))}
          </ul>
        ) : null}

        {hasMore && onLoadMore ? (
          <Button
            type="button"
            variant="outline"
            className="min-h-11 w-full"
            disabled={isLoadingMore}
            onClick={onLoadMore}
            data-testid="receiving-list-load-more"
          >
            {isLoadingMore ? (
              <Loader2
                aria-hidden="true"
                className="size-4 animate-spin motion-reduce:animate-none"
              />
            ) : null}
            {t('admin.receiving.list.loadMore')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
